import { EventEmitter, Injectable, Output } from '@angular/core';
import {
  Agreement,
  BulkCreateProgramCustomerLocationsPath,
  BulkCreateProgramCustomerLocationsRqst,
  BulkProcessStatus,
  CreateAgreementRqst,
  CreateProgramRqst,
  CustomerAgreement,
  DeleteAgreementPath,
  DeleteProgramPath,
  GetCustomerAgreementPath,
  GetCustomerAgreementResp,
  GetProgramPath,
  GetProgramResp,
  Incentive,
  ListCustomerAgreementLocationsResp,
  ListCustomerAgreementsQuery,
  ListCustomerAgreementsResp,
  ListCustomerIncentiveHistoryPath,
  ListCustomerIncentiveHistoryQuery,
  ListCustomerIncentiveHistoryResp,
  ListCustomerProgramLocationsPath,
  ListCustomerProgramLocationsResp,
  OffBillIncentiveApiService,
  Program,
  ProgramCustomerLocation,
  UpdateAgreementPath,
  UpdateAgreementRqst,
  UpdateProgramPath,
  UpdateProgramRqst,
} from '@xpo-ltl/sdk-offbillincentive';
import moment from 'moment';
import { from, map, Observable } from 'rxjs';
import { Location } from '@models/location.model';
import { Util } from '@enums/util.enum';
import { HelperService } from '@services/helper.service';

@Injectable({
  providedIn: 'root',
})
export class AgreementListService {
  @Output() deleteActionEmitter$ = new EventEmitter<boolean>();
  queryParams = new ListCustomerAgreementsQuery();
  queryParamsCache = new ListCustomerAgreementsQuery();
  expireCache = false;
  agreementLocationCache = new Observable<ListCustomerAgreementLocationsResp>();
  activeFilters: boolean = false;
  filters: any = new ListCustomerAgreementsQuery();
  bulkResults: Array<BulkProcessStatus> = [];
  periodBeginDate: Date;
  periodEndDate: Date;
  obiIncentiveId: string;
  obiIncentiveNbr: string;

  get getPeriodEndDate(): Date {
    return this.periodEndDate;
  }

  get getPeriodBeginDate(): Date {
    return this.periodBeginDate;
  }

  constructor(
    private obiApiService: OffBillIncentiveApiService,
    private helperService: HelperService
  ) {}

  deleteActionEmitter() {
    this.deleteActionEmitter$.next(true);
  }

  buildProgram(program: Program, data: any): Program {
    program.aggregatePeriodCd = data.aggregatePeriodCd;
    program.combinedLocationInd = data.combinedLocationInd;
    program.deferredDiscountInd = data.deferredDiscountInd;
    program.deferredDiscountPercentage = data.deferredDiscountPercentage;
    program.inboundCollectCd = data.inboundCollectCd ? Util.shortYes : Util.shortNo;
    program.inboundCollectRate = data.inboundCollectRate;
    program.inboundPrepaidCd = data.inboundPrepaidCd ? Util.shortYes : Util.shortNo;
    program.inboundPrepaidRate = data.inboundPrepaidRate;
    program.collectInd = data.collectInd ? data.collectInd : false;
    program.collectRate = data.collectRate;
    program.prepaidInd = data.prepaidInd ? data.prepaidInd : false;
    program.prepaidRate = data.prepaidRate;
    program.maxDaysToPay = data.maxDaysToPay;
    program.maxDiscountPercentage = data.maxDiscountPercentage;
    program.maxDiscountRevenueAmount = data.maxDiscountRevenueAmount;
    program.outboundCollectCd = data.outboundCollectCd ? Util.shortYes : Util.shortNo;
    program.outboundCollectRate = data.outboundCollectRate;
    program.outboundPrepaidCd = data.outboundPrepaidCd ? Util.shortYes : Util.shortNo;
    program.outboundPrepaidRate = data.outboundPrepaidRate;
    program.payOnCd = data.payOnCd;
    program.programEffectiveDate = new Date(data.programEffectiveDate);
    program.programExpiryDate = new Date(data.programExpiryDate ?? Util.DefaultDate);
    program.qualifyOnCd = data.qualifyOnCd;
    program.renewProgramInd = data.renewProgramInd;
    program.spottedTrailerInd = data.spottedTrailerInd;
    program.thirdPartyCd = data.thirdPartyCd ? Util.shortYes : Util.shortNo;
    program.thirdPartyRate = data.thirdPartyRate;
    program.verifyShipmentPaidInd = data.verifyShipmentPaidInd;
    program.interStateInd = data.interStateInd || false;
    program.intraStateInd = data.intraStateInd || false;
    program.canadianInd = data.canadianInd || false;
    program.interStateAmcAmount = data.interStateInd ? data.interStateAmcAmount : null;
    program.intraStateAmcAmount = data.intraStateInd ? data.intraStateAmcAmount : null;
    program.canadianAmcAmount = data.canadianInd ? data.canadianAmcAmount : null;
    program.exceedToPayInd = data.exceedToPayInd;
    program.commonRate = data.commonRate;
    program.programComment = data.programComment;
    return program;
  }

  requestAgreementList(): Observable<CustomerAgreement[]> {
    this.queryParamsCache = this.queryParams;
    return this.obiApiService.listCustomerAgreements(this.queryParams).pipe(
      map((incentiveListResp: ListCustomerAgreementsResp) => {
        incentiveListResp.customerAgreements.forEach((element: any) => {
          const daysToExpire = this.renderExpireDate(element.agreement);
          element.obiAgreementId = element.agreement.obiAgreementId;
          element.expireSoon = daysToExpire && daysToExpire < 31 ? Util.yes : Util.no;
          element.expired = !daysToExpire;
          element.expireDays = daysToExpire;
          element.effectiveDate = element.agreement.effectiveDate;
          element.expiryDate = element.agreement.expiryDate;
          element.agreement.customerName = this.helperService.removeExtraWhiteSpaces(element.agreement.customerName);
          element.address = element.address ? element.address : {};
          element.address.cityName = this.helperService.removeExtraWhiteSpaces(element.address.cityName);
        });
        return incentiveListResp.customerAgreements;
      })
    );
  }

  validateEffectiveDate(newDateValue: string, oldDateValue?: string): { isValid: boolean; errorMsg?: string } {
    const newDate = new Date(newDateValue);
    let errorMsg: string = null;
    if (isNaN(newDate.getTime())) {
      errorMsg = `Invalid Effective date format - ${newDateValue}`;
      return { isValid: false, errorMsg: errorMsg }; // Reject the change
    }
    if (oldDateValue) {
      const oldDate = new Date(oldDateValue);
      const enteredDate = moment(newDate).startOf('day');
      const effectiveDate = moment(oldDate).startOf('day');
      const twoYearsAgo = moment().subtract(2, 'years').startOf('day');

      if (enteredDate.isSameOrAfter(effectiveDate)) {
        errorMsg = 'Agreement can only be backdated. Date cannot be after the current agreement effective date. ';
        return { isValid: false, errorMsg: errorMsg }; // Reject the change
      }
      if (enteredDate.isBefore(twoYearsAgo)) {
        errorMsg = 'Backdate cannot be more than 2 years in the past from todays date';
        return { isValid: false, errorMsg: errorMsg }; // Reject the change
      }
    }
    return { isValid: true, errorMsg: errorMsg };
  }

  validateRenegotiateDate(
    newValue: string,
    currentEffectiveDate: Date,
    currentExpiryDate: Date
  ): { isValid: boolean; errorMsg?: string } {
    const newDate = new Date(newValue);
    let errorMsg: string = null;
    if (isNaN(newDate.getTime())) {
      errorMsg = `Invalid Renegotiate date format - ${newValue}`;
      return { isValid: false, errorMsg: errorMsg }; // Reject the change
    }
    const enteredDate = moment(newDate).startOf('day');
    const effectiveDate = moment(new Date(currentEffectiveDate)).startOf('day');
    const expiryDate = moment(currentExpiryDate).startOf('day');
    if (enteredDate.isBefore(effectiveDate)) {
      errorMsg = 'Renegotiate date cannot be before Agreement Effective date';
      return { isValid: false, errorMsg: errorMsg }; // Reject the change
    }
    return { isValid: true, errorMsg: errorMsg };
  }

  validateExpirationDate(
    newValue: string,
    currentEffectiveDate: string,
    currentRenegDate: string
  ): { isValid: boolean; errorMsg?: string } {
    const newDate = new Date(newValue);
    let errorMsg: string = null;
    if (isNaN(newDate.getTime())) {
      errorMsg = `Invalid date format - ${newValue}`;
      return { isValid: false, errorMsg: errorMsg }; // Reject the change
    }
    const enteredDate = moment(newDate).startOf('day');
    const effectiveDate = moment(new Date(currentEffectiveDate)).startOf('day');
    const renegDate = moment(new Date(currentRenegDate)).startOf('day');

    if (enteredDate.isBefore(effectiveDate)) {
      errorMsg = 'Expire date cannot be before Agreement Effective Date';
      return { isValid: false, errorMsg: errorMsg }; // Reject the change
    }
    return { isValid: true, errorMsg: errorMsg };
  }

  private renderExpireDate(input: any): any {
    const firstDate = moment(new Date());
    const secondDate = moment(input.expiryDate);
    const difference = secondDate.diff(firstDate, 'days');
    return !isNaN(difference) && difference >= 0 ? difference + 1 : '';
  }

  setQueryParameters(query: ListCustomerAgreementsQuery): any {
    this.queryParams = query;
    this.filters = query;
    this.periodBeginDate = this.queryParams.periodBeginDate;
    this.periodEndDate = this.queryParams.periodEndDate;
  }

  requestAgreement(obiAgreementId): Observable<GetCustomerAgreementResp> {
    const pathParams = new GetCustomerAgreementPath();
    pathParams.obiAgreementId = obiAgreementId;
    return this.obiApiService.getCustomerAgreement(pathParams).pipe(
      map((response) => {
        const effDt = this.helperService.transformDate(response.customerAgreement.agreement.effectiveDate);
        const exprDt = this.helperService.transformDate(response.customerAgreement.agreement.expiryDate);
        response.customerAgreement.agreement.agreementComment =
          response.customerAgreement.agreement.agreementComment || '';
        response.customerAgreement.agreement.expiryDate = exprDt;
        response.customerAgreement.agreement.effectiveDate = effDt;
        return response;
      })
    );
  }

  requestAgreementHistory(customerCd): Observable<Incentive[]> {
    const pathParams = new ListCustomerIncentiveHistoryPath();
    pathParams.madCode = customerCd;
    const queryParams = new ListCustomerIncentiveHistoryQuery();

    return this.obiApiService.listCustomerIncentiveHistory(pathParams, queryParams).pipe(
      map((response: ListCustomerIncentiveHistoryResp) => {
        return response.incentives;
      })
    );
  }

  requestAgreementLocations(obiAgreementId): Observable<ListCustomerAgreementLocationsResp> {
    const pathParams = new GetCustomerAgreementPath();
    if (obiAgreementId) {
      pathParams.obiAgreementId = obiAgreementId;
      this.agreementLocationCache = this.obiApiService.listCustomerAgreementLocations(pathParams);
    }
    return this.agreementLocationCache;
  }

  requestAgreementProgramLocations(obiProgramId): Observable<ListCustomerProgramLocationsResp> {
    const pathParams = new ListCustomerProgramLocationsPath();
    pathParams.obiProgramId = obiProgramId;
    return this.obiApiService.listCustomerProgramLocations(pathParams).pipe(
      map((response: ListCustomerProgramLocationsResp) => {
        response.customerProgramLocations.forEach((item) => {
          if (!item.programCustomerLocation.discountRate) {
            item.programCustomerLocation.discountRate = 0;
          }
        });
        return response;
      })
    );
  }

  requestProgramDetails(obiProgramId): Observable<GetProgramResp> {
    const pathParams = new GetProgramPath();
    pathParams.obiProgramId = obiProgramId;
    return this.obiApiService.getProgram(pathParams);
  }

  createAgreement(agreement: Agreement) {
    const request = new CreateAgreementRqst();
    request.agreement = agreement;
    const dataOption = { toastOnError: false };
    return this.obiApiService.createAgreement(request, dataOption);
  }

  updateAgreement(agreementId: number, agreement: Agreement, actionType: string) {
    const request = new UpdateAgreementRqst();
    request.agreement = agreement;
    request.actionType = actionType;
    const pathParams = new UpdateAgreementPath();
    pathParams.obiAgreementId = agreementId;
    const dataOption = { toastOnError: false };
    return this.obiApiService.updateAgreement(request, pathParams, dataOption).pipe(
      map((response) => {
        const exprDt = this.helperService.transformDate(response.agreement.expiryDate);
        response.agreement.expiryDate = exprDt;
        return response;
      })
    );
  }

  deleteAgreement(agreementId: number) {
    const pathParams = new DeleteAgreementPath();
    pathParams.obiAgreementId = agreementId;
    const dataOption = { toastOnError: false };
    return this.obiApiService.deleteAgreement(pathParams, dataOption);
  }

  createProgram(obiGreementId: number, program: Program) {
    const request = new CreateProgramRqst();
    request.obiAgreementId = obiGreementId;
    request.program = program;
    const dataOption = { toastOnError: false };
    return this.obiApiService.createProgram(request, dataOption);
  }

  deleteProgram(programId: number) {
    const pathParams = new DeleteProgramPath();
    pathParams.obiProgramId = programId;
    const dataOption = { toastOnError: false };
    return this.obiApiService.deleteProgram(pathParams, dataOption);
  }

  updateProgram(program: Program, actionType: string) {
    const request = new UpdateProgramRqst();
    request.program = program;
    request.actionType = actionType;
    const pathParams = new UpdateProgramPath();
    pathParams.obiProgramId = program.obiProgramId;
    const dataOption = { toastOnError: false };
    return this.obiApiService.updateProgram(request, pathParams, dataOption);
  }

  updateProgramLocations(program: Program, programLocations: Location[]) {
    const request: BulkCreateProgramCustomerLocationsRqst = new BulkCreateProgramCustomerLocationsRqst();
    request.programCustomerLocations = this.convertLocationToProgramCustomerLocation(programLocations, program);
    const pathParams: BulkCreateProgramCustomerLocationsPath = new BulkCreateProgramCustomerLocationsPath();
    pathParams.obiProgramId = program.obiProgramId;
    const dataOption = { toastOnError: false, loadingOverlayEnabled: true };
    return this.obiApiService.bulkCreateProgramCustomerLocations(request, pathParams, dataOption);
  }

  convertLocationToProgramCustomerLocation(locations: Location[], program: Program): ProgramCustomerLocation[] {
    const pcLocation: ProgramCustomerLocation[] = locations.map((value: Location) => {
      const pcl = new ProgramCustomerLocation();
      pcl.madCode = value.AccCode;
      pcl.effectiveDate = new Date(program.programEffectiveDate);
      pcl.expiryDate = new Date(program.programExpiryDate);
      pcl.discountRate = program.commonRate ? program.commonRate : 0;
      pcl.billMessageInd = true;
      return pcl;
    });
    return pcLocation;
  }

  mockBulkResults(): Observable<BulkProcessStatus[]> {
    return from([this.bulkResults].length ? [this.bulkResults] : []);
  }

  /**
   * Create a list of results from bulk process request
   *
   * @param results - response from request
   * @param notChunk - bulk process does not aggregate results
   *
   * @example
   * unset `notChunk` or set it as false, to aggregate values
   * from bulk process response, ie: progressbar handles
   * multiple request with chunked data
   */
  setBulkResults(results: BulkProcessStatus[], notChunk?: boolean) {
    this.bulkResults = notChunk ? results : [...this.bulkResults, ...results];
  }

  setObiIncentiveId(incentiveId) {
    this.obiIncentiveId = incentiveId;
  }

  getObiIncentiveId(): string | boolean {
    return !(this.obiIncentiveId === '0') ? this.obiIncentiveId : false;
  }

  setObiIncentiveNbr(incentiveNbr) {
    this.obiIncentiveNbr = incentiveNbr;
  }

  getObiIncentiveNbr(): string | boolean {
    return !(this.obiIncentiveNbr === '0') ? this.obiIncentiveNbr : false;
  }
}
